import { useCallback } from 'react';
import { fetchQuery, graphql } from 'react-relay';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import getRelayEnvironment from '@atlassian/jira-relay-environment/src/index.tsx';
import type { useGetRealtimeIssueDetailsQuery as IssueDetails } from '@atlassian/jira-relay/src/__generated__/useGetRealtimeIssueDetailsQuery.graphql.ts';
import { PACKAGE_NAME, TEAM_NAME } from '../../common/constants.tsx';
import type {
	IssueSearchInputType,
	SearchViewContextInputType,
	GetRealtimeIssueDetailsResponse,
} from './types.tsx';

export const useGetRealtimeIssueDetails = () => {
	return useCallback(
		async ({
			issues,
			cloudId,
			fieldSetIds,
			isInlineEditingEnabled,
			isDensityFull,
			isHierarchyEnabled,
			isGroupingEnabled,
			isHideDoneItemsEnabled,
			isConditionalFormattingEnabled,
			issueSearchInput,
			searchViewContextInput,
			projectKey,
			isTimelineEnabled,
			isLinkRelationshipFieldEnabled,
			isEditableSyncLoaded,
		}: {
			issues: {
				issueAri: string;
				issueId: string;
			}[];
			cloudId: string;
			fieldSetIds: string[];
			isInlineEditingEnabled: boolean;
			isDensityFull: boolean;
			isHierarchyEnabled: boolean | undefined;
			isConditionalFormattingEnabled: boolean;
			isGroupingEnabled: boolean;
			isHideDoneItemsEnabled: boolean;
			issueSearchInput: IssueSearchInputType;
			searchViewContextInput: SearchViewContextInputType;
			projectKey: string;
			isStatusTrackingEnabled: boolean | undefined;
			isTimelineEnabled: boolean;
			isLinkRelationshipFieldEnabled: boolean;
			isEditableSyncLoaded: boolean;
		}): Promise<GetRealtimeIssueDetailsResponse> => {
			const environment = getRelayEnvironment();

			try {
				// if the issue is in a loaded context, the issue is visible so we need to fetch the fields for display
				const result = await fetchQuery<IssueDetails>(
					environment,
					graphql`
						query useGetRealtimeIssueDetailsQuery(
							$cloudId: ID!
							$issueAris: [ID!]!
							$issueChanges: [JiraIssueChangeInput!]!
							$fieldSetIds: [String!]!
							$isInlineEditingEnabled: Boolean!
							$isDensityFull: Boolean!
							$isHierarchyEnabled: Boolean!
							$staticViewInput: JiraIssueSearchStaticViewInput!
							$issueSearchInput: JiraIssueSearchInput!
							$searchViewContextInput: JiraIssueSearchViewContextInput!
							$projectKey: String!
							$isConditionalFormattingEnabled: Boolean!
							$isTimelineEnabled: Boolean!
							$isLinkRelationshipFieldEnabled: Boolean!
							$isEditableSyncLoaded: Boolean!
						) {
							# We make a deferred request for issues here, as we need to throttle requests for issue details from the subscriptions.
							# We leave fields unused here as we need them to populate the store, but not necessarily use them in the controller.
							jira_issuesByIds(ids: $issueAris) {
								__id
								issueId
								parentIssueField {
									parentIssue {
										__id
									}
								}
								fieldSetsById(fieldSetIds: $fieldSetIds, first: 500) {
									__id
									# We need to ensure all updated fields data is fetched, so we reuse the fragment spread from the issue row component.
									# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
									...IssueRow_fieldSets
										@arguments(
											isInlineEditingEnabled: $isInlineEditingEnabled
											isDensityFull: $isDensityFull
											isConditionalFormattingEnabled: $isConditionalFormattingEnabled
											isTimelineEnabled: $isTimelineEnabled
											isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
											isEditableSyncLoaded: $isEditableSyncLoaded
										)
								}
								# eslint-disable-next-line @atlassian/relay/unused-fields
								key
								# eslint-disable-next-line @atlassian/relay/unused-fields
								issueTypeField @include(if: $isHierarchyEnabled) {
									id
									issueType {
										hierarchy {
											level
										}
									}
								}
								# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
								...progressSummary_issueViewChildIssuesPanel_statusAndResolved
								# eslint-disable-next-line @atlassian/relay/unused-fields
								canHaveChildIssues(projectKey: $projectKey) @include(if: $isHierarchyEnabled)
							}
							jira {
								searchViewContexts(
									cloudId: $cloudId
									issueChanges: $issueChanges
									issueSearchInput: $issueSearchInput
									staticViewInput: $staticViewInput
									searchViewContextInput: $searchViewContextInput
								) @optIn(to: "JiraListComponent-M1.2") {
									edges {
										node {
											... on JiraIssueSearchBulkViewParentContexts {
												parentIssueId
											}
											... on JiraIssueSearchBulkViewGroupContexts {
												jql
											}
											contexts {
												sourceIssueId @required(action: THROW)
												position @required(action: THROW)
												beforeIssueId
												afterIssueId
											}
										}
									}
								}
							}
						}
					`,
					{
						issueAris: issues.map((issue) => issue.issueAri),
						cloudId,
						fieldSetIds,
						isInlineEditingEnabled,
						isDensityFull,
						issueSearchInput,
						searchViewContextInput,
						isHierarchyEnabled: isHierarchyEnabled ?? false,
						projectKey,
						issueChanges: issues.map((issue) => ({ issueId: issue.issueId })),
						isConditionalFormattingEnabled,
						staticViewInput: {
							isHierarchyEnabled,
							isGroupingEnabled,
							isHideDoneEnabled: isHideDoneItemsEnabled,
						},
						isTimelineEnabled,
						isLinkRelationshipFieldEnabled,
						isEditableSyncLoaded,
					},
					{ fetchPolicy: 'network-only' },
				).toPromise();

				return {
					issues:
						result?.jira_issuesByIds?.filter(Boolean)?.map((issue) => ({
							issueId: issue.issueId,
							issueRecordId: issue.__id,
							fieldSetsRecordId: issue.fieldSetsById?.__id,
							parentIssueRecordId: issue.parentIssueField?.parentIssue?.__id,
						})) ?? [],
					searchViewContexts:
						result?.jira?.searchViewContexts?.edges
							?.map((edge) => {
								if (!edge?.node) {
									return undefined;
								}
								return {
									parentIssueId: edge.node.parentIssueId ?? undefined,
									jql: edge.node.jql ?? undefined,
									contexts:
										edge?.node?.contexts?.map((context) => ({
											position: context.position,
											sourceIssueId: context.sourceIssueId,
											beforeIssueId: context.beforeIssueId,
											afterIssueId: context.afterIssueId,
										})) ?? [],
								};
							})
							.filter(Boolean) ?? [],
				};
			} catch (error) {
				fireErrorAnalytics({
					meta: {
						id: 'realTimeFetchIssueFieldSetConnection',
						packageName: PACKAGE_NAME,
						teamName: TEAM_NAME,
					},
					error: error instanceof Error ? error : undefined,
					sendToPrivacyUnsafeSplunk: true,
				});

				return Promise.resolve({ issues: [], searchViewContexts: [] });
			}
		},
		[],
	);
};
